import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { array, bool, func, number, oneOfType, string } from 'prop-types'
import { pushGtmData } from '@jelly/utils'

// Styled Components via Emotion
const TableOfContentsContainer = styled.div`
	padding-top: 10px;
	position: ${({ isSticky, isFooterVisible }) =>
		!isFooterVisible && isSticky ? 'fixed' : 'relative'}; /* support IE11 */
	position: sticky;
	top: 0;

	* {
		box-sizing: content-box !important;
	}

	/* only want to set the top for IE11 */
	@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
		top: ${({ isFooterVisible, topOffset }) => (isFooterVisible ? `${topOffset}px` : 0)};
	}
`
const ElementWrapper = styled.a`
	cursor: pointer;
	display: flex;
	margin-top: 12px;
	position: relative;
	width: auto;
`
const ElementMark = styled.div`
	background-color: ${({ isActive, theme }) => (isActive ? theme.colors.primary : '#FFFFFF')};
	border: 2px solid #fff;
	border-radius: 18px;
	display: inline-block;
	height: 10px;
	line-height: 1.5rem;
	margin: auto 12px auto 0;
	max-width: 10px;
	min-height: 10px;
	min-width: 10px;
	position: relative;
	width: 10%;

	:after {
		content: '';
		position: absolute;
		top: -4px;
		left: -4px;
		right: -4px;
		bottom: -4px;
		background: ${({ theme }) => theme.tableOfContents.bulletBorderColor};
		border-radius: 18px;
		z-index: -1;
	}
`
const Element = styled.div`
	display: inline-block;
	color: ${({ isActive, theme }) => (isActive ? theme.colors.tertiary : theme.colors.gray[500])};
	line-height: 1.5rem;
	max-width: 165px;
	vertical-align: middle;
	width: 90%;

	&:hover {
		color: ${({ theme }) => theme.tableOfContents.hoverColor};
	}
`
const TocLine = styled.div`
	background: ${({ theme }) => theme.tableOfContents.lineColor};
	bottom: 0;
	${({ bottomOffsetHeight, topOffsetHeight }) =>
		`height: calc((100% - ${bottomOffsetHeight + topOffsetHeight}px) - 25px);
		margin-bottom: ${bottomOffsetHeight}px;
		`}; /* - 25px to account for padding */
	margin-left: 7px;
	position: absolute;
	width: 1px;
	z-index: -2;
`

TableOfContents.propTypes = {
	activeTocElement: string,
	isFooterVisible: bool,
	isSticky: bool,
	tocElements: array.isRequired,
	topOffset: oneOfType([func, number]),
}

export function TableOfContents({ activeTocElement, docType, isFooterVisible, isSticky, tocElements = [], topOffset }) {
	const [topOffsetHeight, setTopOffsetHeight] = useState()
	const [bottomOffsetHeight, setBottomOffsetHeight] = useState()
	const [isTocPresent, setIsTocPresent] = useState()

	useEffect(() => {
		const handleTocLineHeight = () => {
			setTopOffsetHeight(
				document.getElementById('toc-element-0')
					? document.getElementById('toc-element-0').offsetHeight / 2
					: 25
			)
			setIsTocPresent(document.getElementById('toc-block').offsetHeight !== 0 ? true : false)
			const lastTocNum = `toc-element-${tocElements.length - 1}`
			setBottomOffsetHeight(
				document.getElementById(lastTocNum) && document.getElementById(lastTocNum).offsetHeight / 2
			)
		}
		handleTocLineHeight()
		window.addEventListener('resize', handleTocLineHeight)

		return () => {
			// Clean up
			window.removeEventListener('resize', handleTocLineHeight)
		}
	}, [tocElements.length])

	/**
	 * Programmatically prevents default jump link functionality and
	 * scrolls to the link instead. Also pushes a tracking event.
	 * @param {Event} e
	 */
	const handleClick = (e) => {
		e.preventDefault()
		document.getElementById(e.currentTarget.hash.slice(1)).scrollIntoView()
		pushGtmData({
			event: 'gaEvent',
			gaEventCategory: 'TOC Click',
			gaEventAction: docType || 'Content Page',
			gaEventLabel: window.location,
			gaEventNoninteraction: false,
		})
	}

	return (
		<TableOfContentsContainer
			id="toc-block"
			isFooterVisible={isFooterVisible}
			isSticky={isSticky}
			topOffset={topOffset}
		>
			{tocElements.map((element, i) => {
				// setActiveElement will check to see if activeTocElement is set
				// if it is an empty string, set the activeTocElement to the first link
				// if the activeTocElement is the same as the element.id the section in the viewport is the active link
				const setActiveElement =
					activeTocElement === '' ? tocElements[0].id : activeTocElement === element.id ? element.id : ''
				const isActiveElement = setActiveElement === element.id
				return (
					<ElementWrapper href={`#${element.id}`} id={`toc-element-${i}`} key={i} onClick={handleClick}>
						<ElementMark isActive={isActiveElement}></ElementMark>
						<Element isActive={isActiveElement}>
							<div>{element.title}</div>
						</Element>
					</ElementWrapper>
				)
			})}
			{isTocPresent && <TocLine bottomOffsetHeight={bottomOffsetHeight} topOffsetHeight={topOffsetHeight} />}
		</TableOfContentsContainer>
	)
}
